import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { countries, countryName } from "../lib/countries";
import { meQuery } from "../lib/store";
import { signupIntent } from "../lib/signup";
import { TopBar } from "../components/Shell";
import { Banner, Button, Card, cx, Field, fieldError, Input, Select } from "../components/ui";

const services = ["theme_development", "store_setup", "marketing", "integrations"] as const;

// Attribution captured from the landing URL (utm_*, ref) survives the login redirect via sessionStorage.
function attribution() {
  try {
    const raw = sessionStorage.getItem("sooquna.attribution");
    if (raw) return JSON.parse(raw);
  } catch { /* unavailable */ }
  return undefined;
}

export function Onboarding() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useQuery(meQuery);
  const [type, setType] = useState<"merchant" | "partner">(() => (signupIntent().type === "partner" ? "partner" : "merchant"));
  const [name, setName] = useState("");
  const [country, setCountry] = useState("QA");
  const [phone, setPhone] = useState("");
  const [sellingNow, setSellingNow] = useState<boolean | null>(null);
  const [industry, setIndustry] = useState("");
  const [company, setCompany] = useState("");
  const [website, setWebsite] = useState("");
  const [svc, setSvc] = useState<string[]>([]);
  const [agree, setAgree] = useState(false);

  const save = useMutation({
    mutationFn: () => api<{ org_id: string; next: string }>("/admin/v1/onboarding/profile", {
      body: {
        type, name: name || me.data?.name, country, phone, locale,
        merchant: type === "merchant" ? { selling_now: sellingNow, industry } : undefined,
        partner: type === "partner" ? { company, website, services: svc, accept_agreement: agree } : undefined,
        attribution: attribution(),
      },
    }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ["orgs"] });
      if (r.next === "partner_dashboard") navigate({ to: "/partners/$org", params: { org: r.org_id } });
      else navigate({ to: "/stores/new" });
    },
  });
  const err = save.error;

  return (
    <div className="min-h-full">
      <TopBar />
      <form className="mx-auto max-w-xl space-y-4 px-4 py-8" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div>
          <h1 className="text-xl font-semibold">{t("onboarding.title")}</h1>
          <p className="text-sm text-stone-600">{t("onboarding.subtitle")}</p>
        </div>
        {err && !(err instanceof ApiError && err.errors.length) && <Banner tone="critical">{err.message}</Banner>}
        <Card>
          <fieldset className="space-y-4">
            <legend className="mb-2 text-sm font-medium">{t("onboarding.type")}</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              {(["merchant", "partner"] as const).map((k) => (
                <button type="button" key={k} onClick={() => setType(k)} aria-pressed={type === k}
                  className={cx("rounded-xl border p-3 text-start", type === k ? "border-stone-900 ring-2 ring-brand" : "border-stone-300 hover:bg-stone-50")}>
                  <div className="font-medium">{t(`onboarding.${k}`)}</div>
                  <div className="text-sm text-stone-600">{t(`onboarding.${k}_hint`)}</div>
                </button>
              ))}
            </div>
            <Field label={t("onboarding.name")} error={fieldError(err, "name")}>
              <Input value={name} placeholder={me.data?.name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("onboarding.country")}>
                <Select value={country} onChange={(e) => setCountry(e.target.value)}>
                  {countries.map((c) => <option key={c.code} value={c.code}>{countryName(c, locale)}</option>)}
                </Select>
              </Field>
              <Field label={t("onboarding.phone")} optional error={fieldError(err, "phone")}>
                <Input type="tel" dir="ltr" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+974…" autoComplete="tel" />
              </Field>
            </div>
            {type === "merchant" ? (
              <>
                <div className="space-y-1">
                  <span className="text-sm font-medium">{t("onboarding.selling_now")}</span>
                  <div className="flex gap-2">
                    {[true, false].map((v) => (
                      <Button type="button" key={String(v)} variant={sellingNow === v ? "primary" : "secondary"} onClick={() => setSellingNow(v)}>
                        {t(v ? "onboarding.yes" : "onboarding.no")}
                      </Button>
                    ))}
                  </div>
                </div>
                <Field label={t("onboarding.industry")} optional><Input value={industry} onChange={(e) => setIndustry(e.target.value)} /></Field>
              </>
            ) : (
              <>
                <Field label={t("onboarding.company")} error={fieldError(err, "partner.company")}><Input value={company} onChange={(e) => setCompany(e.target.value)} required /></Field>
                <Field label={t("onboarding.website")} optional><Input type="url" dir="ltr" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" /></Field>
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium">{t("onboarding.services")}</legend>
                  {services.map((s) => (
                    <label key={s} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={svc.includes(s)} onChange={(e) => setSvc(e.target.checked ? [...svc, s] : svc.filter((x) => x !== s))} />
                      {t(`onboarding.service.${s}`)}
                    </label>
                  ))}
                </fieldset>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} /> {t("onboarding.agreement")}
                </label>
              </>
            )}
          </fieldset>
        </Card>
        <div className="flex justify-end"><Button variant="primary" type="submit" loading={save.isPending}>{t("common.continue")}</Button></div>
      </form>
    </div>
  );
}
